import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { cn, formatLargeNumber } from 'lib/helpers/utility';
import { updatePreferences } from 'lib/context/TableContext/helper';
import { useTableContext } from 'lib/context/TableContext/TableContext';

import Button from '../Button/Button';
import Popover from '../Popovers/Popover/Popover';
import Typography from '../Typography/Typography';

const limitOptions = [
  { label: '15', value: 15 },
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
  { label: '500', value: 500 }
];

const CustomStatusBarCount = React.memo(({ count = 0, page: pageProp, pagination }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    name,
    colDefs,
    limit = 25,
    setLimit,
    setPage,
    page: pageFromContext = 1
  } = useTableContext();

  const page = pageProp ?? pageFromContext;

  const totalPages = Math.ceil(count / limit); // Found a bug where we can filter if we are based on a page where the items fetched cannot reach that fill out of pages
  // therefore we return items total to it's extend point i.e TotalPages | CONSULT BEFORE REMOVING!

  const currentPage = page > totalPages ? totalPages : page;
  const start = pagination ? (currentPage - 1) * limit + 1 : 1;
  const end = pagination ? Math.min(start + limit - 1, count) : count;

  const onLimitChangeHandler = useCallback(
    async (value) => {
      setLimit(value);
      setPage(1);
      await updatePreferences({ navigate, cols: colDefs, limit: value, queryClient, name });
      queryClient.invalidateQueries(['table-limit']);
    },
    [setLimit, setPage, navigate, colDefs, queryClient, name]
  );

  if (!count || count === 0) return <div className="hidden" />;

  return (
    <div className="flex h-full items-center justify-end gap-1 whitespace-nowrap p-0 pr-[38px] xs:hidden xs:pr-0">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1">
          <Typography variant="label" size="small" weight="medium" color="neutral-700">
            Rows per page
          </Typography>
          <Popover
            customButtonTrigger={({ open }) => (
              <Button
                variant="tertiary"
                size="small"
                text={limit}
                iconRight="chevron-down-linear"
                iconShade={700}
                color="neutral"
                iconSize={16}
                className={cn(
                  'h-6 w-fit min-w-[55px] bg-white py-0 pl-2 pr-1',
                  open && 'bg-neutral-100'
                )}
              />
            )}
            optionArrowIcon="chevron-right-filled"
            optionIconColor="neutral"
            iconsShade={700}
            options={limitOptions}
            onOptionClick={(option, handleClose) => {
              onLimitChangeHandler(option.value);
              handleClose();
            }}
            textClassName="text-neutral-900"
            isFixed
            position="top"
          />
        </div>
        <div className="flex items-center gap-1">
          <Typography variant="label" size="small" weight="medium" color="neutral-700">
            {formatLargeNumber(start)} - {formatLargeNumber(end)}
          </Typography>
          <Typography variant="label" size="small" color="neutral-500">
            of
          </Typography>
          <Typography variant="label" size="small" weight="medium" color="neutral-700">
            {formatLargeNumber(count)}
          </Typography>
        </div>
      </div>
    </div>
  );
});

export { CustomStatusBarCount };
export default CustomStatusBarCount;
